// src/component/hooks/useApiService.js
import axios from 'axios';
import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSnackbar } from '../providers/SnackbarProvider';

const REFRESH_ENDPOINT = 'auth/refresh'; // relative path; baseURL ile birleşir

// Base URL'yi normalize eder: sonda tek '/' kalır (ör. 'https://host/api/')
const normalizeBaseUrl = (raw) => {
  if (!raw) return 'http://localhost:8080/api/';
  const s = String(raw).trim();
  return s.replace(/\/+$/, '') + '/';
};

const useApiService = (baseUrl) => {
  // Öncelik: parametre > env > localhost
  const envBase = process.env.REACT_APP_API_URL; // Örn: https://client-room.onsafx.com/api
  const apiBaseUrl = normalizeBaseUrl(baseUrl ?? envBase ?? 'http://localhost:8080/api/');

  // Origin: eğer base '/api' ile bitiyorsa onu at, yoksa olduğu gibi kullan
  // Sonrasında sonda '/' kalmayacak şekilde normalize et
  const apiOrigin = apiBaseUrl.replace(/\/api\/?$/, '').replace(/\/+$/, '');

  const navigate = useNavigate();
  const { showSnackbar } = useAppSnackbar();

  // --- Storage helpers ---
  const getAccessToken = () => sessionStorage.getItem('token');
  const getRefreshToken = () => sessionStorage.getItem('refresh_token');
  const saveTokens = ({ access_token, refresh_token }) => {
    if (access_token) sessionStorage.setItem('token', access_token);
    if (refresh_token) sessionStorage.setItem('refresh_token', refresh_token);
  };
  const clearTokens = () => {
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('refresh_token');
  };

  /**
   * Returns authentication headers with optional custom headers merged in.
   * Does not set Content-Type here — it will be set per request depending on body type.
   */
  const getAuthHeaders = (customHeaders = {}) => {
    const token = getAccessToken();
    return token ? { Authorization: `Bearer ${token}`, ...customHeaders } : { ...customHeaders };
  };

  /**
   * Resolves a URL:
   * - Absolute URLs (http/https) → returned as-is
   * - Root-relative URLs (start with "/") → prefixed with apiOrigin
   * - Relative URLs → handled by axios instance baseURL
   */
  const resolveUrl = (url) => {
    if (!url) return apiBaseUrl; // Fallback to base
    if (/^https?:\/\//i.test(url)) return url;
    if (url.startsWith('/')) return `${apiOrigin}${url}`; // apiOrigin sondan '/', url baştan '/' — birleşim güvenli
    return url;
  };

  /**
   * Create a dedicated axios instance to avoid global interceptor side effects.
   */
  const client = useMemo(() => {
    return axios.create({
      baseURL: apiBaseUrl,
      timeout: 15000, // 15s timeout for better UX
    });
  }, [apiBaseUrl]);

  /**
   * A separate axios instance for refresh (no response interceptors attached),
   * so the refresh call itself 401 alırsa sonsuz döngü olmaz.
   */
  const refreshClient = useMemo(() => {
    return axios.create({
      baseURL: apiBaseUrl,
      timeout: 15000,
    });
  }, [apiBaseUrl]);

  /**
   * Attach a response interceptor to handle token expiration (401) globally.
   * The interceptor is attached on mount and removed on unmount.
   */
  useEffect(() => {
    let isRefreshing = false;
    let refreshPromise = null;
    const refreshSubscribers = [];

    const subscribeTokenRefresh = (cb) => {
      refreshSubscribers.push(cb);
    };

    const onRefreshed = (newToken) => {
      while (refreshSubscribers.length) {
        const cb = refreshSubscribers.shift();
        try { cb(newToken); } catch { /* ignore */ }
      }
    };

    const refreshAccessToken = async () => {
      const rToken = getRefreshToken();
      if (!rToken) return null;

      try {
        const res = await refreshClient.post(REFRESH_ENDPOINT, {
          refresh_token: rToken,
        });
        const { access_token, refresh_token } = res?.data || {};
        if (!access_token) return null;

        saveTokens({ access_token, refresh_token });
        return access_token;
      } catch (err) {
        console.error('Refresh token failed', err?.response?.data || err?.message);
        return null;
      }
    };

    const resInterceptor = client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const status = error?.response?.status;
        const errCode = error?.response?.data?.error;
        const originalRequest = error?.config;

        if (!originalRequest) return Promise.reject(error);

        if (status === 401 && errCode === 'invalid_credentials') {
          return Promise.reject(error);
        }

        if (status === 401 && !originalRequest._retry) {
          if (isRefreshing) {
            return new Promise((resolve, reject) => {
              subscribeTokenRefresh((newToken) => {
                if (!newToken) {
                  reject(error);
                  return;
                }
                originalRequest._retry = true;
                originalRequest.headers = {
                  ...(originalRequest.headers || {}),
                  Authorization: `Bearer ${newToken}`,
                };
                resolve(client.request(originalRequest));
              });
            });
          }

          // refresh’i bu istek başlatsın
          isRefreshing = true;
          originalRequest._retry = true;

          try {
            if (!refreshPromise) {
              refreshPromise = refreshAccessToken();
            }
            const newToken = await refreshPromise;

            isRefreshing = false;
            refreshPromise = null;

            onRefreshed(newToken);

            if (newToken) {
              originalRequest.headers = {
                ...(originalRequest.headers || {}),
                Authorization: `Bearer ${newToken}`,
              };
              return client.request(originalRequest);
            }

            // refresh başarısız → oturum kapat
            clearTokens();
            showSnackbar('Oturum süresi doldu. Lütfen tekrar giriş yapın.', 'error');
            navigate('/login');
            return Promise.reject(error);
          } catch (e) {
            isRefreshing = false;
            refreshPromise = null;
            onRefreshed(null);
            clearTokens();
            showSnackbar('Oturum süresi doldu. Lütfen tekrar giriş yapın.', 'error');
            navigate('/login');
            return Promise.reject(e);
          }
        }

        return Promise.reject(error);
      }
    );

    return () => {
      client.interceptors.response.eject(resInterceptor);
    };
  }, [client, navigate, showSnackbar, refreshClient]);

  /**
   * Extracts a user-friendly error message from an API error response.
   */
  const extractApiError = (error, fallback = 'Beklenmeyen bir hata oluştu.') => {
    const code = error?.response?.data?.error;
    if (!code) return fallback;
    switch (code) {
      case 'invalid_payload': return 'Form alanlarını geçerli şekilde doldurunuz.';
      case 'not_found': return 'Kayıt bulunamadı.';
      case 'server_error': return 'Sunucu hatası oluştu.';
      case 'invalid_credentials': return 'E‑posta veya şifre geçersiz.';
      default: return fallback;
    }
  };

  /**
   * Generic GET request.
   */
  const executeGet = async ({ url, setStateDelegate = () => {}, headers = {}, params = {} }) => {
    const finalUrl = resolveUrl(url);
    try {
      const res = await client.get(finalUrl, { headers: getAuthHeaders(headers), params });
      setStateDelegate(res?.data?.items ?? res?.data);
      return res?.data;
    } catch (err) {
      console.error('GET error:', err?.response?.data || err?.message);
      throw err;
    }
  };

  /**
   * Shared helper for POST, PUT, PATCH requests.
   * Automatically handles Content-Type depending on whether body is FormData.
   */
  const withBody = async (method, { url, data, setStateDelegate = () => {}, headers = {}, params }) => {
    const finalUrl = resolveUrl(url);
    try {
      const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;
      const safeHeaders = getAuthHeaders(headers);

      if (isFormData) {
        // Let browser/axios set multipart/form-data boundary
        delete safeHeaders['Content-Type'];
        delete safeHeaders['content-type'];
      } else {
        safeHeaders['Content-Type'] = safeHeaders['Content-Type'] || 'application/json';
      }

      const res = await client.request({ method, url: finalUrl, data, headers: safeHeaders, params });
      setStateDelegate(res?.data);
      return res?.data;
    } catch (err) {
      console.error(`${method.toUpperCase()} error:`, err?.response?.data || err?.message);
      throw err;
    }
  };

  const executePost = (args) => withBody('post', args);
  const executePut   = (args) => withBody('put', args);
  const executePatch = (args) => withBody('patch', args);

  /**
   * DELETE request with optional request body.
   */
  const executeDelete = async ({ url, data, setStateDelegate = () => {}, headers = {}, params }) => {
    const finalUrl = resolveUrl(url);
    try {
      const res = await client.delete(finalUrl, {
        data,
        headers: getAuthHeaders(headers),
        params,
      });
      setStateDelegate(res?.data);
      return res?.data;
    } catch (err) {
      console.error('DELETE error:', err?.response?.data || err?.message);
      throw err;
    }
  };

  /**
   * GET request that returns a Blob.
   * Useful for downloading images, files, etc.
   */
  const executeGetBlob = async ({ url, headers = {}, params = {} }) => {
    const finalUrl = resolveUrl(url);
    try {
      const res = await client.get(finalUrl, {
        headers: getAuthHeaders(headers),
        params,
        responseType: 'blob',
      });
      return res?.data;
    } catch (err) {
      console.error('BLOB GET error:', err?.response?.data || err?.message);
      throw err;
    }
  };

  return {
    executeGet,
    executePost,
    executePut,
    executeDelete,
    executePatch,
    executeGetBlob,
    extractApiError,
    client,
  };
};

export default useApiService;
